import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import * as api from '../api/wishlist'

const WishlistContext = createContext(null);

export function WishlistProvider({ children }){
  const { token, user } = useAuth();
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wishlist') || '[]'); } catch (e) { return []; }
  });

  // ensure guestId exists (match CartContext behaviour) - initialize synchronously
  const getGuestId = () => {
    try {
      let g = localStorage.getItem('guestId');
      if (!g) {
        g = `guest_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
        localStorage.setItem('guestId', g);
      }
      return g;
    } catch (e) {
      return `guest_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    }
  };

  // Also run once on mount for consistency
  useEffect(() => {
    getGuestId();
  }, []);

  useEffect(() => {
    try { localStorage.setItem('wishlist', JSON.stringify(wishlist)); } catch (e) {}
  }, [wishlist]);

  const ensureId = (p) => p._id || p.id || p.basic?.internalSKU || `${Date.now()}_${Math.random().toString(36).slice(2,9)}`;

  // Sync wishlist on login: merge local items into server, then load server wishlist
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const guestId = getGuestId();
        if (token) {
          // push local wishlist items to server (merge guest list)
          const local = JSON.parse(localStorage.getItem('wishlist') || '[]');
          for (const it of local) {
            try {
              await api.addToWishlist({ productId: it._id || it.id, productLink: it.productLink, token });
            } catch (e) {}
          }
          // load server wishlist
          const res = await api.getWishlist({ token });
          if (mounted && res && res.items) {
            const mapped = res.items.map(i => {
              const prod = i.product || {};
              const price = prod.pricing?.price ?? null;
              const discountedPrice = prod.pricing?.discountedPrice ?? i.discountedPriceAtAdd ?? null;
              const discountPercent = (prod.pricing && prod.pricing.price && prod.pricing.discountedPrice)
                ? Math.round(((prod.pricing.price - prod.pricing.discountedPrice) / prod.pricing.price) * 100)
                : null;
              const rating = prod.rating ?? null;
              const ratingCount = prod.reviewsCount ?? null;
              return {
                _id: i._id,
                productId: prod._id || prod.id || i.product,
                name: prod.basic?.productName || prod.name || i.productName || '',
                photo: prod.media?.mainImage || prod.photo || i.productPhoto || '',
                price,
                discountedPrice,
                discountPercent,
                rating,
                ratingCount
              };
            });
            setWishlist(mapped);
          }
          // clear local guest wishlist
          try { localStorage.removeItem('wishlist'); } catch (e) {}
        } else {
          // guest: fetch server wishlist using guestId and merge with local storage
          if (!guestId) return;
          const res = await api.getWishlistGuest({ guestId });
          if (mounted && res && res.items) {
            const mapped = res.items.map(i => {
              const prod = i.product || {};
              const price = prod.pricing?.price ?? null;
              const discountedPrice = prod.pricing?.discountedPrice ?? i.discountedPriceAtAdd ?? null;
              const discountPercent = (prod.pricing && prod.pricing.price && prod.pricing.discountedPrice)
                ? Math.round(((prod.pricing.price - prod.pricing.discountedPrice) / prod.pricing.price) * 100)
                : null;
              const rating = prod.rating ?? null;
              const ratingCount = prod.reviewsCount ?? null;
              return {
                _id: i._id,
                productId: prod._id || prod.id || i.product,
                name: prod.basic?.productName || prod.name || i.productName || '',
                photo: prod.media?.mainImage || prod.photo || i.productPhoto || '',
                price,
                discountedPrice,
                discountPercent,
                rating,
                ratingCount
              };
            });
            setWishlist(mapped);
          }
        }
      } catch (err) {
        console.error('Wishlist sync error', err);
      }
    })();
    // listen for explicit login events to sync immediately
    const onAuthLogin = (e) => {
      (async () => {
        try {
          const tokenLocal = e && e.detail && e.detail.token ? e.detail.token : token;
          if (tokenLocal) {
            const local = JSON.parse(localStorage.getItem('wishlist') || '[]');
            for (const it of local) {
              try { await api.addToWishlist({ productId: it._id || it.id, productLink: it.productLink, token: tokenLocal }); } catch (err) {}
            }
            const res = await api.getWishlist({ token: tokenLocal });
            if (mounted && res && res.items) {
              const mapped = res.items.map(i => {
                const prod = i.product || {};
                return {
                  _id: i._id,
                  productId: prod._id || prod.id || i.product,
                  name: prod.basic?.productName || prod.name || i.productName || '',
                  photo: prod.media?.mainImage || prod.photo || i.productPhoto || '',
                  discountedPrice: prod.pricing?.discountedPrice ?? i.discountedPriceAtAdd ?? null
                };
              });
              setWishlist(mapped);
            }
            try { localStorage.removeItem('wishlist'); } catch (err) {}
          }
        } catch (err) {
          console.error('Wishlist auth:login sync error', err);
        }
      })();
    };
    window.addEventListener('auth:login', onAuthLogin);
    return () => { mounted = false; window.removeEventListener('auth:login', onAuthLogin); };
  }, [token, user]);

  const addToWishlist = (p) => {
    const id = ensureId(p);
    const productId = p._id || p.id || id;
    console.log('[Wishlist] addToWishlist called:', { id, productId, p });
    (async () => {
      const guestId = getGuestId();
      console.log('[Wishlist] guestId:', guestId, 'token:', !!token);
      if (token) {
        try {
          console.log('[Wishlist] Calling API addToWishlist (authenticated)...');
          const res = await api.addToWishlist({ productId: p._id || p.id, productLink: p.productLink, token });
          console.log('[Wishlist] API response:', res);
          if (res && res.item) {
            // add mapped server item
            setWishlist(prev => {
              if (prev.find(x => x._id === res.item._id)) return prev;
              const prod = res.item.product || {};
              return [...prev, {
                _id: res.item._id,
                productId: prod._id || prod.id || res.item.product,
                name: prod.basic?.productName || prod.name || res.item.productName || '',
                photo: prod.media?.mainImage || prod.photo || res.item.productPhoto || '',
                discountedPrice: prod.pricing?.discountedPrice ?? res.item.discountedPriceAtAdd ?? null
              }];
            });
            return;
          }
        } catch (err) {
          console.error('addToWishlist error', err);
        }
      } else {
        // guest flow: send guestId
        try {
          console.log('[Wishlist] Calling API addToWishlistGuest (guest)...', { productId: p._id || p.id, guestId });
          const res = await api.addToWishlistGuest({ productId: p._id || p.id, productLink: p.productLink, guestId });
          console.log('[Wishlist] Guest API response:', res);
          if (res && res.item) {
            const prod = res.item.product || {};
            setWishlist(prev => {
              if (prev.find(x => x._id === res.item._id)) return prev;
              return [...prev, {
                _id: res.item._id,
                productId: prod._id || prod.id || res.item.product,
                name: prod.basic?.productName || prod.name || res.item.productName || '',
                photo: prod.media?.mainImage || prod.photo || res.item.productPhoto || '',
                discountedPrice: prod.pricing?.discountedPrice ?? res.item.discountedPriceAtAdd ?? null
              }];
            });
            return;
          }
        } catch (err) {
          console.error('addToWishlist guest error', err);
        }
      }
      // fallback to local-only
      setWishlist(prev => {
        if (prev.find(x => x._id === id || x.productId === productId)) return prev;
        return [...prev, { ...p, _id: id, productId, name: p.basic?.productName || p.name || '', photo: p.media?.mainImage || p.photo || '', discountedPrice: p.pricing?.discountedPrice ?? null }];
      });
    })();
  };

  const removeFromWishlist = (id) => {
    if (!id) return;
    (async () => {
      let found = null;
      try {
        const guestId = getGuestId();
        // id may be either wishlist-item id (server doc) or product id. Resolve to wishlist item id when needed.
        let targetId = id;
        found = wishlist.find(x => x._id === id || x.productId === id);
        if (found && found._id) targetId = found._id;
        if (token) await api.removeFromWishlist({ id: targetId, token }); else await api.removeFromWishlistGuest({ id: targetId, guestId });
      } catch (err) {
        console.error('removeFromWishlist error', err);
      }
      setWishlist(prev => prev.filter(x => (x._id !== (found && found._id ? found._id : id)) && x.productId !== id));
    })();
  };

  const isInWishlist = (id) => {
    if (!id) return false;
    // check either wishlist document id or the productId snapshot stored on the wishlist item
    return !!wishlist.find(x => x._id === id || x.productId === id);
  };

  const toggleWishlist = (p) => {
    const id = ensureId(p);
    if (isInWishlist(id)) removeFromWishlist(id);
    else addToWishlist({ ...p, _id: id });
  };

  const count = wishlist.length;

  return (
    <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, isInWishlist, toggleWishlist, count }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist(){ return useContext(WishlistContext); }
